/* SeasonChip — Phase 62.6 (May 8, 2026)
 *
 * Tiny eyebrow chip on the Verdict masthead that names the local season
 * for the user's location. Indian users see Indian seasons (Summer /
 * Monsoon / Post-monsoon / Winter), other latitudes see the standard
 * meteorological four (Spring / Summer / Autumn / Winter), with the
 * Southern hemisphere flipped automatically.
 *
 * The chip exists to give the rest of the page implicit context — the
 * Decision Card behaves differently in May (heatwave country) vs August
 * (monsoon), and a one-word eyebrow tells the user *why* the verdict
 * looks the way it does without saying it explicitly.
 */
import { Leaf, Sun, CloudRain, Snowflake, Wind } from "@phosphor-icons/react";

/** India-specific (subset of subcontinent + SE Asia uses similar split). */
const IN_SEASONS = [
  { months: [12, 1, 2], key: "winter",       label: "Winter",       Icon: Snowflake, tone: "#1e40af" },
  { months: [3, 4, 5],  key: "summer",       label: "Summer",       Icon: Sun,       tone: "#ea580c" },
  { months: [6, 7, 8, 9], key: "monsoon",    label: "Monsoon",      Icon: CloudRain, tone: "#0284c7" },
  { months: [10, 11],   key: "post-monsoon", label: "Post-monsoon", Icon: Wind,      tone: "#0891b2" },
];

/** Standard meteorological seasons for the Northern hemisphere. */
const NH_SEASONS = [
  { months: [12, 1, 2], key: "winter", label: "Winter", Icon: Snowflake, tone: "#1e40af" },
  { months: [3, 4, 5],  key: "spring", label: "Spring", Icon: Leaf,      tone: "#16a34a" },
  { months: [6, 7, 8],  key: "summer", label: "Summer", Icon: Sun,       tone: "#ea580c" },
  { months: [9, 10, 11], key: "autumn", label: "Autumn", Icon: Leaf,     tone: "#b45309" },
];

/** Southern hemisphere uses the same four labels but flipped. */
const SH_SEASONS = [
  { months: [12, 1, 2], key: "summer", label: "Summer", Icon: Sun,       tone: "#ea580c" },
  { months: [3, 4, 5],  key: "autumn", label: "Autumn", Icon: Leaf,      tone: "#b45309" },
  { months: [6, 7, 8],  key: "winter", label: "Winter", Icon: Snowflake, tone: "#1e40af" },
  { months: [9, 10, 11], key: "spring", label: "Spring", Icon: Leaf,     tone: "#16a34a" },
];

/** India's bounding box (rough, includes Sri Lanka + Bangladesh).
 * Latitude 6°-37° N, Longitude 68°-97° E. Used to apply IN_SEASONS. */
function isIndiaLike(lat, lon) {
  if (lat == null || lon == null) return false;
  return lat >= 5 && lat <= 38 && lon >= 67 && lon <= 98;
}

function pickSeason(seasons, month) {
  return seasons.find((s) => s.months.includes(month)) ?? seasons[0];
}

export default function SeasonChip({ location, theme, testId = "season-chip" }) {
  const lat = location?.latitude;
  const lon = location?.longitude;
  const month = new Date().getMonth() + 1; // 1-12

  let seasons;
  if (isIndiaLike(lat, lon)) seasons = IN_SEASONS;
  else if (lat != null && lat < 0) seasons = SH_SEASONS;
  else seasons = NH_SEASONS;

  const season = pickSeason(seasons, month);
  if (!season) return null;
  const { Icon, label, tone } = season;

  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[9px] font-extrabold uppercase tracking-[0.18em]"
      style={{
        background: `${tone}14`,
        color: tone,
        border: `1px solid ${tone}33`,
      }}
    >
      <Icon size={10} weight="fill" color={tone} />
      {label}
    </span>
  );
}
